<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Header -->
    <header class="bg-[#0a4068] shadow-2xl border-b-4 border-blue-300">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
          <div class="flex items-center space-x-3 sm:space-x-5 w-full sm:w-auto">
            <div class="relative">
              <div class="absolute inset-0 bg-white rounded-full blur-sm opacity-30"></div>
              <img :src="logoUrl" alt="AUAS Logo" class="relative h-12 w-12 sm:h-16 sm:w-16 flex-shrink-0 rounded-full border-3 border-white shadow-lg transform hover:scale-105 transition-transform duration-300" />
            </div>
            <div class="w-full sm:w-auto">
              <h1 class="text-xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-wide drop-shadow-2xl">
                <span class="text-white font-extrabold">AUAS</span>
                <span class="text-white"> Staff Portal</span>
              </h1>
              <p class="text-sm sm:text-lg text-white mt-1 font-medium drop-shadow-xl">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                  Welcome, {{ auth.user?.email }}
                </span>
              </p>
            </div>
          </div>
          <button
            @click="handleLogout"
            class="w-full sm:w-auto bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg shadow-xl hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-red-300/50 text-sm sm:text-base font-bold transition-all duration-300 transform hover:scale-105 whitespace-nowrap border-2 border-red-400/30"
          >
            <span class="flex items-center justify-center">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
              </svg>
              Logout
            </span>
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="px-4 py-6 sm:px-0">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          <!-- Form Status Card -->
          <div class="bg-white overflow-hidden shadow rounded-lg">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <svg class="h-8 w-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate">
                      Form Status
                    </dt>
                    <dd class="text-lg font-medium text-gray-900">
                      <span v-if="formStatus === 'draft'" class="text-yellow-600">Draft</span>
                      <span v-else-if="formStatus === 'in_progress'" class="text-blue-600">In Progress</span>
                      <span v-else-if="formStatus === 'submitted'" class="text-green-600">Submitted</span>
                      <span v-else class="text-gray-500">Not Started</span>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-5 py-3">
              <div class="text-sm">
                <router-link
                  to="/form"
                  class="font-medium text-blue-600 hover:text-blue-500"
                >
                  {{ formStatus === 'submitted' ? 'Update Information' : 'Continue form' }}
                </router-link>
              </div>
            </div>
          </div>

          <!-- Quick Stats Card -->
          <!-- <div class="bg-white overflow-hidden shadow rounded-lg">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <svg class="h-8 w-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate">
                      Completion
                    </dt>
                    <dd class="text-lg font-medium text-gray-900">
                      {{ completionPercentage }}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 px-5 py-3">
              <div class="text-sm">
                <span class="text-gray-500">
                  {{ completedSections }} of {{ totalSections }} sections
                </span>
              </div>
            </div>
          </div> -->

          <!-- Last Activity Card -->
          <div class="bg-white overflow-hidden shadow rounded-lg">
            <div class="p-5">
              <div class="flex items-center">
                <div class="flex-shrink-0">
                  <svg class="h-8 w-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm font-medium text-gray-500 truncate">
                      Last Activity
                    </dt>
                    <dd class="text-lg font-medium text-gray-900">
                      <span v-if="lastActivity">
                        {{ formatDate(lastActivity) }}
                      </span>
                      <span v-else class="text-gray-500">Never</span>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Instructions -->
        <div class="mt-8 bg-white shadow rounded-lg">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              Getting Started
            </h3>
            <div class="mt-2 max-w-xl text-sm text-gray-500">
              <p>
                Complete your staff information form by filling out all required sections. 
                Your progress is automatically saved as you work.
              </p>
            </div>
            <div class="mt-5">
              <router-link
                to="/form"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Start Form
                <svg class="ml-2 -mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '../stores/auth';
import { useForm } from '../stores/form';
import { sections } from '../config/sections';

const router = useRouter();
const auth = useAuth();
const form = useForm();
const logoUrl = '/AUAS.png';

const formStatus = ref('draft');
const lastActivity = ref<number | null>(null);

const totalSections = computed(() => sections.length);
const completedSections = computed(() => {
  return Object.keys(form.sections).filter(key => {
    const section = form.sections[key];
    return section && Object.keys(section).length > 0;
  }).length;
});

const completionPercentage = computed(() => {
  if (totalSections.value === 0) return 0;
  return Math.round((completedSections.value / totalSections.value) * 100);
});

const formatDate = (timestamp: number) => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const handleLogout = async () => {
  await auth.logout();
  router.push('/login');
};

onMounted(async () => {
  try {
    await form.bootstrap();
    formStatus.value = form.status;
    lastActivity.value = form.lastSaved;
  } catch (error) {
    // console.error('Failed to load form data:', error);
  }
});
</script>
